import { AbstractControl } from '@angular/forms';
import { catchError, EMPTY, map, Observable, of } from 'rxjs';

import { SimulationDao } from '@app/dao/simulation/simulation.dao';
import { FormControlOption } from '@app/forms/subscription/definition/interface/formControlOption.interface';
import { cashOrCreditOfScpiOwnership } from './product-form/simulation-packs-scpi/scpi-ownership-financing';

/**
 * Indique si le produit souscrit propose des modèles d'allocation : `null` quand ils n'ont pas pu être chargés, aucune
 * valeur quand le dossier n'a pas encore de produit.
 */
export function pledgeProductHasPacks(
  simulationDao: SimulationDao,
  pledge: any,
): Observable<boolean | null> {
  const productId = pledge?.products?.[0]?.productId;
  if (!productId) {
    return EMPTY;
  }
  return hasAnyPack(
    simulationDao.getProductPacks(
      pledge.productType === 'PER' ? 'PER' : 'ASSURANCE_VIE',
      String(productId),
    ),
  );
}

/**
 * Indique si l'organisation propose des packs SCPI dans le mode de financement du dossier : `null` quand ils n'ont pas
 * pu être chargés.
 */
export function pledgeHasScpiPacks(
  simulationDao: SimulationDao,
  pledge: any,
): Observable<boolean | null> {
  return hasAnyPack(
    simulationDao.getScpiPacks(
      cashOrCreditOfScpiOwnership(pledge?.parameters?.scpiOwnershipType),
    ),
  );
}

function hasAnyPack(packs$: Observable<any[]>): Observable<boolean | null> {
  return packs$.pipe(
    map(packs => !!packs?.length),
    catchError(() => of(null)),
  );
}

/**
 * Paramètres du dossier qui portent le choix de répartition d'un produit.
 */
export const productParameters = (pledge: any) =>
  pledge?.products?.[0]?.parameters;

/**
 * Paramètres du dossier qui portent le choix de sélection des SCPI.
 */
export const pledgeParameters = (pledge: any) => pledge?.parameters;

/**
 * Efface un choix « pack » devenu impossible, dans le formulaire et dans le dossier en cours : la question est de nouveau
 * à répondre parmi les options disponibles, y compris pour la navigation vers la question obligatoire suivante.
 */
export function forgetUnavailablePackChoice(
  form: AbstractControl | null | undefined,
  name: string,
  subscriptionFacade: { getPledge(): any; setPledge(pledge: any): void },
  savedParameters: (pledge: any) => any,
): void {
  const control = form?.get(name);
  if (control?.value !== 'PACK') {
    return;
  }
  control.setValue(null);
  const pledge = subscriptionFacade.getPledge();
  const parameters = savedParameters(pledge);
  if (parameters?.[name] === 'PACK') {
    parameters[name] = null;
    subscriptionFacade.setPledge(pledge);
  }
}

const SUFFIXES = ['A', 'B', 'C'];

/**
 * Renumérote les options de répartition affichées pour que leurs lettres se suivent.
 */
export function withSequentialSuffixes(
  options: FormControlOption[],
): FormControlOption[] {
  return options.map((option, index) => ({
    ...option,
    suffix: SUFFIXES[index],
  }));
}

/**
 * Renvoie au choix de répartition quand le produit ne propose aucun modèle d'allocation : l'étape des allocations
 * types n'aurait rien à proposer et le choix « pack » enregistré y est effacé.
 */
export function leavePackStepWithoutPack(
  packs: unknown[] | null | undefined,
  subscriptionFacade: { previousStep(): void },
): void {
  if (!packs?.length) {
    subscriptionFacade.previousStep();
  }
}
